import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output
} from '@angular/core';
import {
  NgbActiveModal,
  NgbDateAdapter,
  NgbDateNativeAdapter,
  NgbDateParserFormatter
} from '@ng-bootstrap/ng-bootstrap';
import { OwnerForm } from 'src/app/classes/form-groups/owner-form';
import { Owner } from 'src/app/classes/models';
import { CustomNgbDateFormatter } from 'src/app/dependencies/custom-ngb-date-formatter';
import { AuthService } from 'src/app/services/auth.service';
import { OwnerService } from 'src/app/services/owner.service';
import { ToastService } from 'src/app/services/toast.service';
import { UtilityService } from 'src/app/services/utility.service';

// Can extract owner form from this component and vendor registration step and merge into 1 component
// to be shared by both.
@Component({
  selector: 'app-add-edit-owner-modal',
  templateUrl: './add-edit-owner-modal.component.html',
  styleUrls: ['./add-edit-owner-modal.component.less'],
  providers: [
    { provide: NgbDateAdapter, useClass: NgbDateNativeAdapter },
    { provide: NgbDateParserFormatter, useClass: CustomNgbDateFormatter }
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AddEditOwnerModalComponent {
  constructor(
    private ToastService: ToastService,
    private ActiveModal: NgbActiveModal,
    private UtilityService: UtilityService,
    private OwnerService: OwnerService,
    private AuthService: AuthService,
    private cdr: ChangeDetectorRef
  ) {}

  @Input() Owner?: Owner;
  @Input() country?: 'US' | 'CA';
  @Input() VendorId!: number;

  OwnerFormGroup = new OwnerForm();

  ownerSSNPermissions = [
    'ADMIN_ADMIN',
    'ADMIN_FRAUD_TEAM',
    'ADMIN_PROJECT_MANAGEMENT_TEAM'
  ];

  hasOwnerSSNAccess = false;

  revealedOwnerSSN?: string;

  @Output() ownerSSNRevealedEvent = new EventEmitter<string>();

  viewOwnerSSN(): void {
    if (!this.hasOwnerSSNAccess || !this.Owner?.id) {
      return;
    }

    this.OwnerService.getOwnerSSN({
      OwnerId: this.Owner.id,
      VendorId: this.VendorId
    }).subscribe({
      next: (response) => {
        this.revealedOwnerSSN = response.ssn;
        this.ownerSSNRevealedEvent.emit(response.ssn);
        this.cdr.detectChanges();
      }
    });
  }

  close() {
    this.ActiveModal.dismiss();
  }

  save() {
    const errors = this.UtilityService.generateValidationErrorsObject(
      this.OwnerFormGroup
    );

    if (errors) {
      throw this.ToastService.addErrorToast(
        'Error',
        'There is an error below.',
        { returnError: true }
      );
    }

    const { value: Owner } = this.OwnerFormGroup;

    this.OwnerService.saveOwner(Owner).subscribe({
      next: (SavedOwner) => {
        this.ToastService.addSuccessToast(
          'Success',
          'Successfully saved Owner.'
        );
        this.ActiveModal.close(SavedOwner);
      }
    });
  }

  ngOnInit(): void {
    const { UserAccountRole: { role } = {} } = this.AuthService.getUser();
    if (role && this.ownerSSNPermissions.includes(role)) {
      this.hasOwnerSSNAccess = true;
    }

    if (this.Owner) {
      this.OwnerFormGroup.patchValue(this.Owner);
    }

    if (!this.country) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing Country information.',
        { returnError: true }
      );
    }

    if (!this.VendorId) {
      throw this.ToastService.addErrorToast('Error', 'Missing Vendor Id.', {
        returnError: true
      });
    }

    this.OwnerFormGroup.patchValue({ VendorId: this.VendorId });
  }
}
